import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { makeStyles } from '@mui/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { fromAccounts, fromOrder, useAppDispatch } from '../../store';
import StripeCheckout from '../checkout/StripeCheckout';
import { LoadingProgress } from '../../layout';
import PricingSection from '../../layout/pricing-section/PricingSection';
import { paymentStatus } from '../../layout/routes';
import { useNavigate, useQuery } from '../../routes';
import { Page } from '../../components/page';
import { getFeatureFromPlan, getProductUnit } from '../../models';
import { createFeatures } from '../../models/FeatureConfig';
import { getOrderItemDisplayName } from '../payment-status/orderItemName';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

const useStyles = makeStyles(theme => ({
  container: {
    maxWidth: '1200px',
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    fontSize: '2rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: '1rem',
  },
  paymentSection: {
    padding: theme.spacing(3),
    height: '100%',
  },
  orderSummarySection: {
    padding: theme.spacing(3),
    height: '100%',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1.25rem',
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
  },
  orderItem: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  orderItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(0.5),
  },
  orderItemName: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  orderItemPrice: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  orderItemDetails: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  confirmButton: {
    width: '100%',
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(3),
    fontSize: '1rem',
    fontWeight: 600,
    textTransform: 'none',
  },
  errorAlert: {
    marginBottom: theme.spacing(3),
  },
}));

const ConfirmPaymentPage = () => {
  const classes = useStyles();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { navigate, replaceQuery } = useNavigate();
  const { get } = useQuery();
  const account = useSelector(fromAccounts.selectSelectedAccount);
  const dispatch = useAppDispatch();

  const [error, setError] = useState<string | undefined>(undefined);
  const stripeCheckoutRef = useRef<{ submitPayment:() => void }>(null);

  // Get order data from URL params and store
  const orderId = Number(get('orderId'));
  const orderFromStore = useSelector(fromOrder.selectOrderResponse);
  const [currentOrder, setCurrentOrder] = useState(orderFromStore);
  const orderLoading = useSelector(fromOrder.selectLoading);
  const isOptedInFirstThreeLicenses = useSelector(fromAccounts.selectIsOptedInFirstThreeLicenses);

  const features = createFeatures();

  // Calculate totals like in OrderConfirmationSection but from actual order data
  const calculateOrderSubtotal = () => {
    if (!currentOrder?.orderItems) return 0;
    return currentOrder.orderItems.reduce((sum, item) => (
      sum + (item.totalAmount - item.taxAmount)
    ), 0);
  };

  const calculateTotalDiscount = () => {
    if (!currentOrder?.orderItems) return 0;
    return currentOrder.orderItems.reduce((sum, item) => (
      sum + (item.discountAmount || 0)
    ), 0);
  };

  // Render functions for order summary
  const renderOrderItems = () => {
    if (!currentOrder?.orderItems || currentOrder.orderItems.length === 0) return null;

    // BUNDLE_COMPONENT rows carry no money (the package price is on their parent row) and would
    // render as "$0.00 x 3 licenses" here, so the bundle stays one line on this page.
    const payableItems = currentOrder.orderItems.filter(
      orderItem => orderItem.type !== 'BUNDLE_COMPONENT',
    );

    return (
      <Box>
        {payableItems.map((orderItem, index) => {
          const unitPrice = (orderItem.totalAmount - orderItem.taxAmount) / orderItem.quantity;
          const billingCycle = orderItem.billingCycle === 'YEAR' ? 'year' : 'month';
          const feature = getFeatureFromPlan(orderItem.planId);
          const productUnit = getProductUnit(feature);
          const displayName = getOrderItemDisplayName(intl, orderItem, features);

          return (
            <Box key={orderItem.id || index} className={classes.orderItem}>
              <Box className={classes.orderItemHeader}>
                <Typography className={classes.orderItemName}>
                  {displayName}
                </Typography>
                <Typography className={classes.orderItemPrice}>
                  <FormattedMessage
                    id="payment.order.item.price"
                    values={{
                      amount: intl.formatNumber(
                        orderItem.totalAmount - orderItem.taxAmount,
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                      ),
                    }}
                  />
                </Typography>
              </Box>
              <Box className={classes.orderItemHeader}>
                <Typography className={classes.orderItemDetails}>
                  <FormattedMessage
                    id="payment.order.item.details"
                    values={{
                      unitPrice: intl.formatNumber(
                        unitPrice,
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                      ),
                      quantity: orderItem.quantity,
                      unit: productUnit,
                    }}
                  />
                </Typography>
                <Typography className={classes.orderItemDetails}>
                  <FormattedMessage
                    id="payment.order.item.billing.cycle"
                    values={{
                      billingCycle: billingCycle === 'year' ? 'Annual' : 'Monthly',
                    }}
                  />
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderCreditItems = () => null;

  const renderOrderSummary = () => (
    <Paper className={classes.orderSummarySection} elevation={1}>
      <Box
        display="flex"
        height="100%"
        flexDirection="column"
        justifyContent="space-between"
      >
        <Box>
          <Typography component="h2" className={classes.sectionTitle}>
            <FormattedMessage id="payment.order.summary.title" />
          </Typography>

          {renderOrderItems()}
          {renderCreditItems()}
        </Box>
        <Box>
          <PricingSection
            subTotal={calculateOrderSubtotal()}
            totalDiscount={calculateTotalDiscount()}
            totalTax={currentOrder?.taxAmount || 0}
            totalPrice={currentOrder?.totalAmount || 0}
            showOptInFirstThreeLicenses={isOptedInFirstThreeLicenses}
            showTax
            loading={orderLoading}
          />

          <Button
            variant="contained"
            color="primary"
            className={classes.confirmButton}
            disabled={!currentOrder?.stripeClientSecret || orderLoading}
            onClick={() => {
              stripeCheckoutRef.current?.submitPayment();
            }}
          >
            <FormattedMessage id="payment.confirm.button.text" />
          </Button>
        </Box>
      </Box>
    </Paper>
  );

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId) {
        setError(intl.formatMessage({ id: 'payment.error.order.id.required' }));
        return;
      }

      try {
        // Fetch order details using orderId
        await dispatch(fromOrder.doFetchOrderById({ orderId: Number(orderId) }));
      } catch (err) {
        setError(intl.formatMessage({ id: 'payment.error.fetch.order.details' }));
      }
    };

    fetchOrderDetails().catch();
  }, [orderId, dispatch]);

  useEffect(() => {
    // Validate that we have the necessary order data after fetching
    if (orderLoading) return; // Still loading, don't validate yet

    if (!orderFromStore) {
      setError(intl.formatMessage({ id: 'payment.error.order.data.not.found' }));
      return;
    }

    if (!orderFromStore.stripeClientSecret) {
      setError(intl.formatMessage({ id: 'payment.error.payment.setup.incomplete' }));
      return;
    }
    setError(undefined);
    setCurrentOrder(orderFromStore);
  }, [orderFromStore, orderLoading]);

  const goToPaymentStatusPage = () => {
    // Navigate to payment status page
    navigate(paymentStatus.path, replaceQuery({
      accountId: account.id,
      orderId,
    }));
  };

  const handlePaymentSuccess = () => {
    enqueueSnackbar(
      intl.formatMessage({ id: 'payment.success.message' }),
      { variant: 'success' },
    );

    goToPaymentStatusPage();
  };

  const handlePaymentError = (errorMessage: string) => {
    enqueueSnackbar(errorMessage, { variant: 'error' });
    setError(errorMessage);
  };

  if (orderLoading) {
    return <LoadingProgress fullscreen />;
  }

  if (!currentOrder) {
    return (
      <Box>
        <Container className={classes.container}>
          <Alert severity="error" className={classes.errorAlert}>
            <FormattedMessage id="payment.error.order.not.found" />
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Page
      id="confirm-payment"
      headerId="payment.confirm.page.title"
      fetchData={async () => {
      }}
    >
      <Box
        id="confirm-payment-box"
        key="confirm-payment-box"
        display="flex"
        flexDirection="row"
        height="98%"
      >
        {/* Two Column Layout */}
        <Grid container spacing={3}>
          {/* Left Column - Credit Card Information */}
          <Grid item xs={12} md={8}>
            <Paper className={classes.paymentSection} elevation={1}>
              <Typography className={classes.sectionTitle}>
                <FormattedMessage id="payment.confirm.credit.card.title" />
              </Typography>

              {error && (
                <Alert severity="error" className={classes.errorAlert}>
                  {error}
                </Alert>
              )}

              {currentOrder?.stripeClientSecret && (
                <Elements stripe={stripePromise}>
                  <StripeCheckout
                    ref={stripeCheckoutRef}
                    orderResponse={currentOrder}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                  />
                </Elements>
              )}
            </Paper>
          </Grid>

          {/* Right Column - Order Summary */}
          <Grid item xs={12} md={4}>
            {renderOrderSummary()}
          </Grid>
        </Grid>
      </Box>
    </Page>
  );
};

export default ConfirmPaymentPage;
